import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
} from "@mui/material";
import { useStytch, useStytchUser } from "@stytch/react";
import { isNil, size } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useFormState } from "react-final-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatPhone } from "../_src_shared/utils/functions/formatPhone";
import RffLoanBorrowerExperienceTable from "../components/finalForm/features/RffLoanBorrowerExperienceTable";
import RffAddressInputField from "../components/finalForm/inputs/RffAddressInputField";
import RffBooleanCheckbox from "../components/finalForm/inputs/RffBooleanCheckbox";
import RffDateField from "../components/finalForm/inputs/RffDateField";
import RffPhoneField from "../components/finalForm/inputs/RffPhoneField";
import RffSelectField from "../components/finalForm/inputs/RffSelectField";
import RffSsnField from "../components/finalForm/inputs/RffSsnField";
import RffTextField from "../components/finalForm/inputs/RffTextField";
import RffFormStepper from "../components/finalForm/RffFormStepper";
import RffGroup from "../components/finalForm/shared/RffGroup";
import Flx from "../components/layout/Flx";
import BasicModal from "../components/modals/BasicModal";
import Htag from "../components/typography/Htag";
import Txt from "../components/typography/Txt";
import { selectOptionsCitizenshipStatus } from "../constants/selectOptions/selectOptionsCitizenshipStatus";
import { selectOptionsMaritalStatus } from "../constants/selectOptions/selectOptionsMaritalStatus";
import PhotoIdentificationUploadForm from "../forms/PhotoIdentificationUploadForm";
import { useBorrowerPortalHook } from "../hooks/useBorrowerPortalHook";
import { profileSet } from "../redux/actions/profileActions";
import DashboardLogo from "../assets/DashboardLogo";
import { LogoutOutlined } from "@mui/icons-material";
const LogoutUserButton = ({ children }) => {
  const navigate = useNavigate();
  const stytch = useStytch();
  const handleLogoutUser = useCallback(async () => {
    await stytch.session.revoke();
    navigate("/");
  }, [stytch, navigate]);
  return (
    <Button
      sx={{
        // position: "absolute",
        // left: -8,
        // top: 0,
        fontSize: "12px !important",
        fontWeight: 400,
      }}
      endIcon={
        <LogoutOutlined
          sx={{
            // transform: "rotate(180deg)",
            fontSize: "13px !important",
            // mr: -0.5,
          }}
        />
      }
      color="default"
      variant="text"
      size="small"
      onClick={handleLogoutUser}
    >
      Logout
    </Button>
  );
};
const ProfileSetupWizard = ({ profile }) => {
  const { user } = useStytchUser();
  const [goToPreapproval, setGoToPreapproval] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, updateBorrowerProfile } = useBorrowerPortalHook();
  const isMobile = useMediaQuery("(max-width:768px)");
  const initialValues = useMemo(() => {
    if (size(user.emails) === 1) {
      return {
        smsOptIn: true,
        middleName: profile?.middleName === "test" ? " " : null,
        firstName: profile?.firstName,
        lastName: profile?.lastName,
      };
    }
    return {
      middleName: profile?.middleName === "test" ? " " : null,
      smsOptIn: true,
      firstName: profile?.firstName,
      lastName: profile?.lastName,
    };
  }, [user?.emails, profile]);

  const onSubmit = async (values, form) => {
    updateBorrowerProfile({
      data: { ...values, profileSetupComplete: true },
      onSuccessFn: (d) => {
        dispatch(profileSet(d)); // Use the current form values, not EX_USER_DATA_UPDATED
        navigate("/");
        // if (goToPreapproval) {
        //   dispatch(modalSetCreditAuth());
        // }
        //
      },
    });
  };

  return (
    <BasicModal show maxWidth="md" bodySx={{ p: 3 }}>
      <Flx column fw center gap={2}>
        <Flx fw jb ac sx={{ position: "relative", mb: 1 }}>
          {/* <DashboardLogo showSubtitle height="40px" /> */}
          {isMobile ? (
            <DashboardLogo showSubtitle maxHeight={30} maxWidth={85} />
          ) : (
            <DashboardLogo showSubtitle height="40px" />
          )}
          <LogoutUserButton />
        </Flx>
        <Flx column fw center gap={1} sx={{ mb: 2 }}>
          <Htag tag="h1" sx={{ textAlign: "center" }} className="text-center">
            {/* Complete Your Investor Profile */}
            Investor Profile Setup
          </Htag>
          <Txt secondary sx={{ textAlign: "center" }} className="text-center">
            Complete your profile to streamline preapprovals and loan
            applications.
          </Txt>
          {/* <Txt tag="h1" sx={{ textAlign: "center" }} className="text-center">
            Before creating your first application, let's finising setting up
            your profile.
          </Txt> */}
        </Flx>
        <RffFormStepper
          onSubmit={onSubmit}
          // formSpy
          // progressVariant="linear"
          submitText="Complete Profile"
          loading={loading}
          initialValues={initialValues}
          sx={{ width: "100%" }}
        >
          <RffGroup stepName="Personal" title="Personal Information">
            <RffTextField
              required
              name="firstName"
              label="First Name"
              size={6}
            />
            <RffTextField required name="lastName" label="Last Name" size={6} />
            <RffTextField name="middleName" label="Middle Name" />
            <RffDateField required name="dateOfBirth" label="Date of Birth" />
          </RffGroup>
          <RffGroup
            skippable
            stepName="Background"
            title="Background Information"
            description="Optional, however this information will help expedite the process of creating applications. This can be updated later in your profile."
          >
            <RffPhoneField name="phone" label="Phone" size={6} />
            <RffSsnField label="SSN" name="borrowerSSN" size={6} />
            <RffSelectField
              label="Marital Status"
              size={6}
              name="maritalStatus"
              options={selectOptionsMaritalStatus}
            />
            <RffSelectField
              size={6}
              label="Citizenship Status"
              name="citizenshipStatus"
              options={selectOptionsCitizenshipStatus}
            />
            <RffLoanBorrowerExperienceTable />
          </RffGroup>
          <RffGroup
            stepName="Primary Address"
            title="Primary Address"
            skippable
          >
            <RffAddressInputField
              required
              name="primaryAddress"
              label="Address for where you currently reside"
            />
          </RffGroup>
          <RffGroup
            stepName="Photo ID"
            skippable
            title="Photo Identification"
            disableGrid
          >
            <PhotoIdentificationUploadForm
              autoUpload
              showFilesAfterAutoUpload
              hideLoader
            />
            <RffDateField
              name="idExpirationDate"
              label="Identification Expiration Date"
              suppressGrid
              sx={{ pt: 3 }}
            />
          </RffGroup>
          <ReviewStep
            stepName="Review"
            title="Review Information"
            goToPreapproval={goToPreapproval}
            setGoToPreapproval={setGoToPreapproval}
          />
        </RffFormStepper>
      </Flx>
    </BasicModal>
  );
};

const ReviewStep = ({ goToPreapproval, setGoToPreapproval }) => {
  return (
    <RffGroup title="Please review the following information:" disableGrid>
      <Flx column gap={5}>
        <Box>
          <RffPreviewField name={"firstName"} label={"First Name"} />
          <RffPreviewField name={"middleName"} label={"Middle Name"} />
          <RffPreviewField name={"lastName"} label={"Last Name"} />
          <RffPreviewField
            name={"dateOfBirth"}
            label={"Date of Birth"}
            type="date"
          />
          <RffPreviewField name={"emailAddress"} label={"Email Address"} />
          <RffPreviewField name={"phone"} label={"Phone"} type="phone" />
          {/* <RffPreviewField name={"phone"} label={"Phone"} /> */}
          <RffPreviewField
            name={"primaryAddress"}
            label={"Address"}
            type="address"
          />
          <RffPreviewField
            label={"ID Expiration Date"}
            name={"idExpirationDate"}
            type="date"
          />
        </Box>
        <Flx column gap={1}>
          <RffBooleanCheckbox
            suppressGrid
            name="smsOptIn"
            label="Opt in to SMS Notifications"
          />
          {/* <FormControlLabel
            sx={{
              span: {
                fontSize: "13px !important",
              },
            }}
            control={
              <Checkbox
                checked={goToPreapproval}
                size="small"
                onChange={(event) => {
                  // Immediately trigger change for checkboxes
                  setGoToPreapproval(event.target.checked);
                }}
              />
            }
            label={"Continue to preapproval request after profile setup"}
          /> */}
        </Flx>
      </Flx>
      {/* Proceed to preapproval */}
    </RffGroup>
  );
};

const RffPreviewField = ({ name, label, type }) => {
  const { values } = useFormState();

  const defaultValue = useMemo(() => {
    const val = values[name];

    if (isNil(val)) {
      return null;
    }
    if (type === "date") {
      return new Date(val).toLocaleDateString();
    }

    if (type === "phone") {
      return formatPhone(val);
    }

    if (type === "address") {
      return val?.fullAddress;
    }
    return val;
  }, [values, name, type]);

  const empty = useMemo(() => isNil(defaultValue), [defaultValue]);
  return (
    <Flx gap={0.5} sx={{ mb: 0.8 }}>
      <Txt sx={{ fontSize: "11px", fontWeight: 500 }}>{label}:</Txt>
      <Txt
        sx={{
          fontSize: "11px",
          opacity: empty ? 0.55 : 1,
          fontStyle: empty ? "italic" : "normal",
        }}
      >
        {empty ? "Not Set" : defaultValue}
      </Txt>
    </Flx>
  );
};

export default ProfileSetupWizard;
